import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { DashboardLayout } from "@/pages/Dashboard";
import { useAuth, useLanguage, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { Send, Bot, User, Plus, Loader2, BookOpen } from "lucide-react";

export default function AIAssistant() {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [params] = useSearchParams();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState(params.get("prefill") || "");
  const [pageContext] = useState(params.get("context") || null);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const startNewSession = () => {
    setSessionId(null);
    setMessages([]);
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/ai/chat`, {
        message: userMessage,
        session_id: sessionId,
        page_context: pageContext,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSessionId(response.data.session_id);
      setMessages(prev => [...prev, {
        role: "assistant",
        content: response.data.response,
        sources: response.data.sources || [],
        suggested_services: response.data.suggested_services || [],
      }]);
    } catch (error) {
      toast.error(t("حدث خطأ في المحادثة", "Chat error occurred"));
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const suggestedQuestions = [
    t("كيف أجد سكن في نيويورك؟", "How do I find housing in New York?"),
    t("ما هي أفضل الوظائف للمبتدئين؟", "What are the best jobs for beginners?"),
    t("كيف أحصل على رخصة قيادة؟", "How do I get a driver's license?"),
    t("ما هو نظام الضرائب؟", "What is the tax system?")
  ];

  return (
    <DashboardLayout title={t("المساعد الذكي", "AI Assistant")}>
      <div className="flex flex-col h-[calc(100vh-200px)]" data-testid="ai-assistant">
        {/* Chat Area */}
        <div className="flex-1 glass-card rounded-2xl flex flex-col overflow-hidden">
          {/* Messages */}
          <ScrollArea className="flex-1 p-6">
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-12">
                <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mb-6 animate-pulse-gold">
                  <Bot className="text-yellow-500" size={40} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">
                  {t("مرحباً! أنا مساعدك الذكي", "Hi! I'm your AI assistant")}
                </h3>
                <p className="text-slate-400 mb-8 max-w-md">
                  {t("اسألني أي سؤال عن الحياة في أمريكا", "Ask me anything about life in America")}
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-lg">
                  {suggestedQuestions.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => setInput(q)}
                      className="p-3 text-right glass-card rounded-xl text-sm text-slate-300 hover:border-yellow-500/50 transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {messages.map((msg, index) => (
                  <div
                    key={index}
                    className={`flex gap-4 ${msg.role === "user" ? "flex-row-reverse" : ""}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      msg.role === "user" ? "bg-yellow-500" : "bg-slate-700"
                    }`}>
                      {msg.role === "user" ? (
                        <User className="text-black" size={20} />
                      ) : (
                        <Bot className="text-yellow-500" size={20} />
                      )}
                    </div>
                    <div className={`max-w-[80%] p-4 rounded-2xl ${
                      msg.role === "user"
                        ? "chat-bubble-sent"
                        : "bg-slate-800 text-slate-200 rounded-bl-sm"
                    }`}>
                      <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                      {msg.role === "assistant" && msg.sources && msg.sources.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-slate-700 space-y-2">
                          <p className="text-xs text-yellow-400 flex items-center gap-1"><BookOpen size={11} />{t("المصادر من دليل عرب سند:", "Sources from Arab Sanad Guide:")}</p>
                          <div className="flex flex-wrap gap-2">
                            {msg.sources.map((s, i) => (
                              <Link key={i} to={`/guide/article/${s.slug}`} className="text-xs bg-yellow-500/15 text-yellow-300 hover:bg-yellow-500 hover:text-black px-2 py-1 rounded-md transition" data-testid={`src-${s.slug}`}>
                                {s.title}
                              </Link>
                            ))}
                          </div>
                          {msg.suggested_services && msg.suggested_services.length > 0 && (
                            <div className="flex flex-wrap gap-2 pt-1">
                              {msg.suggested_services.map((svc, i) => (
                                <Link key={i} to={svc} className="text-xs bg-blue-500/15 text-blue-300 hover:bg-blue-500 hover:text-white px-2 py-1 rounded-md transition">
                                  → {svc}
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                      <Bot className="text-yellow-500" size={20} />
                    </div>
                    <div className="bg-slate-800 p-4 rounded-2xl rounded-bl-sm">
                      <Loader2 className="animate-spin text-yellow-500" size={24} />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </ScrollArea>

          {/* Input Area */}
          <div className="p-4 border-t border-slate-800">
            <div className="flex gap-3">
              <Button onClick={startNewSession} variant="outline" className="btn-secondary px-4">
                <Plus size={20} />
              </Button>
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={t("اكتب سؤالك هنا...", "Type your question...")}
                className="flex-1 h-12 bg-slate-900 border-slate-700 text-white focus:border-yellow-500"
                disabled={loading}
                data-testid="ai-input"
              />
              <Button
                onClick={sendMessage}
                disabled={!input.trim() || loading}
                className="btn-primary px-6"
                data-testid="ai-send-btn"
              >
                <Send size={20} />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
